import {
  Button,
  Card,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spacer,
} from '@nextui-org/react';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
} from 'react';
import { FaPlusCircle } from 'react-icons/fa';
import { ICubage } from '../../interfaces/response/Cubage/ICubage';
import { moneyMask } from '../../util/mask';
import useRegisterCubageModal from './hooks/useRegisterCubageModal';

export interface RegisterCubageHandle {
  openToEdit: (cubageAux: ICubage) => void;
  closeModal: () => void;
}

interface RegisterCubageProps {
  onUpdate?: () => void;
  useModalStyle: boolean;
}

const RegisterCubage: ForwardRefRenderFunction<
  RegisterCubageHandle,
  RegisterCubageProps
> = ({ onUpdate, useModalStyle }, ref): JSX.Element => {
  const {
    onOpen,
    isOpen,
    onOpenChange,
    theme,
    handleClose,
    handleSubmit,
    handleOpenEdit,
    handleSubmitCubage,
    errors,
    register,
    isLoading,
    isValid,
    control,
    setValue,
    values,
    cubage,
  } = useRegisterCubageModal({ onUpdate });

  useImperativeHandle(ref, () => ({
    openToEdit: (cubageAux: ICubage) => {
      handleOpenEdit(cubageAux);
    },
    closeModal: () => {
      handleClose();
    },
  }));

  if (!useModalStyle) {
    return (
      <>
        <Button
          onPress={onOpen}
          size="md"
          startContent={
            <div>
              <FaPlusCircle />
            </div>
          }
        >
          <p>Cadastrar cubagem</p>
        </Button>
        <ModalContent>
          <form onSubmit={handleSubmit(handleSubmitCubage)}>
            <ModalHeader className="text-default-600">
              <p>{cubage ? 'Editar' : 'Cadastrar'} cubagem</p>
            </ModalHeader>

            <ModalBody>
              {/* User information */}
              {cubage && (
                <Card
                  className="grid grid-cols-3 bg-foreground-100 p-4"
                  shadow="none"
                >
                  <div>
                    <p className="font-semibold">ID Cubagem:</p>
                    <p className="text-default-600">{cubage.idCubagem}</p>
                  </div>
                </Card>
              )}

              {/* Inputs Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* EAN */}
                <Input
                  size="md"
                  type="text"
                  label="EAN"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  errorMessage={errors.ean?.message}
                  {...register('ean')}
                  value={values.ean}
                  onValueChange={(value) => {
                    setValue('ean', value);
                  }}
                />

                {/* Descrição */}
                <Input
                  size="md"
                  type="text"
                  label="Descrição"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  errorMessage={errors.descricao?.message}
                  {...register('descricao')}
                  value={values.descricao}
                  onValueChange={(value) => {
                    setValue('descricao', value);
                  }}
                />
              </div>
              <Spacer y={1} />
              {/* Inputs Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Altura */}
                <Input
                  size="md"
                  type="text"
                  label="ALTURA"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.altura?.message}
                  {...register('altura')}
                  value={values.altura}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('altura', maskedValue);
                  }}
                />

                {/* Largura */}
                <Input
                  size="md"
                  type="text"
                  label="LARGURA"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.largura?.message}
                  {...register('largura')}
                  value={values.largura}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('largura', maskedValue);
                  }}
                />

                {/* Profundidade */}
                <Input
                  size="md"
                  type="text"
                  label="PROFUNDIDADE"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.profundidade?.message}
                  {...register('profundidade')}
                  value={values.profundidade}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('profundidade', maskedValue);
                  }}
                />
              </div>
            </ModalBody>

            <ModalFooter>
              {/* Cancel Button */}
              <Button color="danger" onPress={handleClose}>
                Cancelar
              </Button>

              {/* Save Button */}
              <Button type="submit" isLoading={isLoading} isDisabled={!isValid}>
                <p>{cubage ? 'Alterar' : 'Cadastrar'}</p>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </>
    );
  }

  return (
    <>
      <Button
        onPress={onOpen}
        size="md"
        startContent={
          <div>
            <FaPlusCircle />
          </div>
        }
      >
        <p>Cadastrar cubagem</p>
      </Button>

      <Modal
        isOpen={isOpen}
        size="4xl"
        onOpenChange={onOpenChange}
        className={`${theme}`}
        onClose={handleClose}
      >
        <ModalContent>
          <form onSubmit={handleSubmit(handleSubmitCubage)}>
            <ModalHeader className="text-default-600">
              <p>{cubage ? 'Editar' : 'Cadastrar'} tabela preço</p>
            </ModalHeader>

            <ModalBody>
              {/* User information */}
              {cubage && (
                <Card
                  className="grid grid-cols-3 bg-foreground-100 p-4"
                  shadow="none"
                >
                  <div>
                    <p className="font-semibold">ID Cubagem:</p>
                    <p className="text-default-600">{cubage.idCubagem}</p>
                  </div>
                </Card>
              )}

              {/* Inputs Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* EAN */}
                <Input
                  size="md"
                  type="text"
                  label="EAN"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  errorMessage={errors.ean?.message}
                  {...register('ean')}
                  value={values.ean}
                  onValueChange={(value) => {
                    setValue('ean', value);
                  }}
                />

                {/* Descrição */}
                <Input
                  size="md"
                  type="text"
                  label="Descrição"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  errorMessage={errors.descricao?.message}
                  {...register('descricao')}
                  value={values.descricao}
                  onValueChange={(value) => {
                    setValue('descricao', value);
                  }}
                />
              </div>
              <Spacer y={1} />
              {/* Inputs Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Altura */}
                <Input
                  size="md"
                  type="text"
                  label="ALTURA"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.altura?.message}
                  {...register('altura')}
                  value={values.altura}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('altura', maskedValue);
                  }}
                />

                {/* Largura */}
                <Input
                  size="md"
                  type="text"
                  label="LARGURA"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.largura?.message}
                  {...register('largura')}
                  value={values.largura}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('largura', maskedValue);
                  }}
                />

                {/* Profundidade */}
                <Input
                  size="md"
                  type="text"
                  label="PROFUNDIDADE"
                  variant="underlined"
                  classNames={{
                    label: 'font-bold',
                  }}
                  startContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-small text-default-400">CM</span>
                    </div>
                  }
                  errorMessage={errors.profundidade?.message}
                  {...register('profundidade')}
                  value={values.profundidade}
                  onValueChange={(value) => {
                    const maskedValue = moneyMask(value, '', 2);
                    setValue('profundidade', maskedValue);
                  }}
                />
              </div>
            </ModalBody>

            <ModalFooter>
              {/* Cancel Button */}
              <Button color="danger" onPress={handleClose}>
                Cancelar
              </Button>

              {/* Save Button */}
              <Button type="submit" isLoading={isLoading} isDisabled={!isValid}>
                <p>{cubage ? 'Alterar' : 'Cadastrar'}</p>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default forwardRef(RegisterCubage);
